import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { useOutsideCheckClick } from 'hooks/UseOnClickOutside'
import { DropdownButton } from 'components/Shared/Button'
import CRMExportModal from 'components/DataIntegrations/ExportModal'
import { useDataIntegrations } from 'components/DataIntegrations/DataIntegrationsProvider'
import { useToast } from 'components/Shared/AppToast/ToastProvider'
import { capitalizeString } from 'utils/helpers'

function parseFileName(value) {
  const regex = /^(.*)-(\d{2}\.\d{2}\.\d{4})\.-(\d{2}:\d{2}):\d{2}$/

  const match = value.match(regex)

  if (!match) {
    return null
  }

  return {
    name: match[1],
    date: match[2],
    time: match[3],
  }
}

const ExportButton = ({
  buttonText,
  buttonClassName,
  dropdownClassName,
  disabled,
  supportedFormats = ['csv', 'external'],
  onExport,
  exportName,
  crmRef,
}) => {
  const { t } = useTranslation()
  const containerRef = useRef()
  const dropdownRef = useRef()
  const [exportResultsOpen, setExportResultsOpen] = useState(false)
  const [crmExportSecretId, setCrmExportSecretId] = useState(null)
  const [crmExportLoading, setCrmExportLoading] = useState(false)
  const { getSupportedCRMs } = useDataIntegrations()
  const { addNotification } = useToast()

  useOutsideCheckClick([containerRef], () => setExportResultsOpen(false), exportResultsOpen)

  useEffect(() => {
    if (exportResultsOpen && dropdownRef.current) {
      dropdownRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'end',
        inline: 'nearest',
      })
    }
  }, [exportResultsOpen])

  const supportedSecrets = supportedFormats.includes('external') ? getSupportedCRMs() : []

  const handleExport = async (format, secretId = null, containerName = null) => {
    if (format === 'crm' && secretId) {
      const secret = supportedSecrets.find((s) => s.id === secretId)
      if (!secret) {
        throw new Error('Secret not found')
      }
      setCrmExportLoading(true)
      setCrmExportSecretId(null)
      try {
        const result = await onExport(format, secretId, containerName)
        if (result.success) {
          addNotification({
            variant: 'success',
            message: t('Toasts.DataIntegrations.export.progress.message', {
              serviceName: capitalizeString(secret.service_name),
            }),
            description: t('Toasts.DataIntegrations.export.progress.description', {
              serviceName: capitalizeString(secret.service_name),
            }),
          })
        } else {
          addNotification({
            variant: 'error',
            message: t([
              `Toasts.DataIntegrations.export.error.${secret.service_name}.message`,
              'Toasts.DataIntegrations.export.error.message',
            ]),
            description: t(
              [
                `Toasts.DataIntegrations.export.error.${secret.service_name}.description`,
                'Toasts.DataIntegrations.export.error.description',
              ],
              {
                serviceName: capitalizeString(secret.service_name),
              },
            ),
          })
        }
      } finally {
        setCrmExportLoading(false)
      }
    } else {
      await onExport(format)
    }
    setExportResultsOpen(false)
  }

  return (
    <div
      className={classNames('export-btn', { 'export-btn--wide': supportedSecrets.length > 0 })}
      ref={containerRef}
    >
      <DropdownButton
        classNames={buttonClassName}
        text={buttonText}
        color="navy"
        isOpen={exportResultsOpen}
        onClick={() => {
          setExportResultsOpen((prev) => !prev)
        }}
        disabled={disabled}
      />
      {exportResultsOpen && (
        <div className="export-dropdown-wrapper">
          <div className={classNames('export-dropdown', dropdownClassName)} ref={dropdownRef}>
            <p className="export-dropdown_item_title">{t('Integrations.send.file')}</p>

            {supportedFormats.includes('csv') && (
              <button onClick={() => handleExport('csv')} className="export-dropdown_item">
                <span className="export-dropdown_item_name">
                  {t('common.csv')}
                </span>
              </button>
            )}

            {supportedFormats.includes('excel') && (
              <button onClick={() => handleExport('excel')} className="export-dropdown_item">
                <span className="export-dropdown_item_name">
                  {t('common.excel')}
                </span>
              </button>
            )}

            <div className="export-dropdown_items_separator" />

            <p className="export-dropdown_item_title">{t('Integrations.send.sendTo')}</p>

            {supportedSecrets.map((secret) => {
              const parsedFileName = parseFileName(secret.name)
              if (parsedFileName) {
                return (
                  <button
                    key={secret.id}
                    onClick={() => setCrmExportSecretId(secret.id)}
                    className="export-dropdown_item"
                  >
                    <p className="export-dropdown_item_name">{parsedFileName.name}</p>

                    <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                      <span className="export-dropdown_item_date">{parsedFileName.date}</span>
                      <span className="export-dropdown_item_separator">•</span>
                      <span className="export-dropdown_item_date">{parsedFileName.time}</span>
                    </div>
                  </button>
                )
              }
            })}
          </div>
        </div>
      )}
      <CRMExportModal
        isOpen={!!crmExportSecretId}
        onClose={() => setCrmExportSecretId(null)}
        serviceName={supportedSecrets.find((s) => s.id === crmExportSecretId)?.service_name || ''}
        exportName={exportName}
        onExport={(containerName) => handleExport('crm', crmExportSecretId, containerName)}
        loading={crmExportLoading}
        ref={crmRef}
      />
    </div>
  )
}

ExportButton.propTypes = {
  buttonText: PropTypes.string.isRequired,
  buttonClassName: PropTypes.string,
  dropdownClassName: PropTypes.string,
  disabled: PropTypes.bool,
  supportedFormats: PropTypes.arrayOf(PropTypes.oneOf(['csv', 'excel'])),
  onExport: PropTypes.func.isRequired,
  exportName: PropTypes.string.isRequired,
  crmRef: PropTypes.object,
}

export default ExportButton
